import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, FileText, FileCode, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ExportDocumentModal({ isOpen, onClose, document }) {
  const [exporting, setExporting] = useState(null);

  // Convert Editor.js content to plain text
  const getPlainText = (content) => {
    try {
      const data = JSON.parse(content);
      const blocks = data.blocks || [];
      let text = '';
      
      blocks.forEach(block => {
        if (block.type === 'header') {
          const level = block.data?.level || 2;
          text += '#'.repeat(level) + ' ' + stripHtml(block.data?.text || '') + '\n\n';
        } else if (block.type === 'paragraph') {
          text += stripHtml(block.data?.text || '') + '\n\n';
        } else if (block.type === 'list') {
          const items = block.data?.items || [];
          const isOrdered = block.data?.style === 'ordered';
          items.forEach((item, idx) => {
            const itemText = typeof item === 'string' ? item : item.content || item.text || '';
            const prefix = isOrdered ? `${idx + 1}. ` : '- ';
            text += prefix + stripHtml(itemText) + '\n';
          });
          text += '\n';
        } else if (block.type === 'checklist') {
          const items = block.data?.items || [];
          items.forEach(item => {
            const check = item.checked ? '[x]' : '[ ]';
            text += `${check} ${stripHtml(item.text || '')}\n`;
          });
          text += '\n';
        } else if (block.type === 'quote') {
          text += `> ${stripHtml(block.data?.text || '')}\n`;
          if (block.data?.caption) text += `> — ${stripHtml(block.data.caption)}\n`;
          text += '\n';
        } else if (block.type === 'code' || block.type === 'codeHighlight') {
          const lang = block.data?.language || '';
          text += '```' + lang + '\n' + (block.data?.code || '') + '\n```\n\n';
        } else if (block.type === 'delimiter') {
          text += '---\n\n';
        } else if (block.type === 'table') {
          const rows = block.data?.content || [];
          rows.forEach((row, idx) => {
            text += '| ' + row.map(cell => stripHtml(cell)).join(' | ') + ' |\n';
            if (idx === 0) {
              text += '| ' + row.map(() => '---').join(' | ') + ' |\n';
            }
          });
          text += '\n';
        }
      });
      
      return text.trim();
    } catch {
      return content || '';
    }
  };

  const stripHtml = (html) => {
    return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format) => {
    if (!document?.content) {
      toast.error('No content to export');
      return;
    }

    setExporting(format);
    try {
      const filename = (document.title || 'document').replace(/[^a-zA-Z0-9]/g, '_');
      
      switch (format) {
        case 'markdown':
          const markdown = getPlainText(document.content);
          downloadFile(markdown, `${filename}.md`, 'text/markdown');
          toast.success('Exported as Markdown');
          break;
          
        case 'mdx':
          const mdxContent = `---\ntitle: "${document.title || 'Untitled'}"\ndate: "${new Date().toISOString()}"\n---\n\n${getPlainText(document.content)}`;
          downloadFile(mdxContent, `${filename}.mdx`, 'text/mdx');
          toast.success('Exported as MDX');
          break;
          
        case 'pdf':
          // For PDF, we'll create a printable HTML and trigger print dialog
          const printWindow = window.open('', '_blank');
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${document.title || 'Document'}</title>
              <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
                h1, h2, h3, h4, h5, h6 { margin-top: 1.5em; margin-bottom: 0.5em; }
                p { line-height: 1.6; margin: 1em 0; }
                code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; }
                pre { background: #f4f4f4; padding: 16px; overflow-x: auto; }
                blockquote { border-left: 3px solid #ddd; margin-left: 0; padding-left: 16px; color: #666; }
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              </style>
            </head>
            <body>
              <h1>${document.title || 'Untitled'}</h1>
              <div>${getPlainText(document.content).replace(/\n/g, '<br>')}</div>
            </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.print();
          toast.success('PDF export dialog opened');
          break;
          
        default:
          toast.error('Unknown format');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
      onClose();
    }
  };

  const exportOptions = [
    {
      format: 'pdf',
      title: 'Export to PDF',
      description: 'Download as a PDF document',
      icon: FileText,
    },
    {
      format: 'mdx',
      title: 'Export to MDX',
      description: 'Download as MDX with frontmatter',
      icon: FileCode,
    },
    {
      format: 'markdown',
      title: 'Export to Markdown',
      description: 'Download as a plain Markdown file',
      icon: FileText,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="export-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Download className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <span>Export document</span>
          </DialogTitle>
          <DialogDescription>
            Choose a format to export "{document?.title || 'this document'}"
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {exportOptions.map(({ format, title, description, icon: Icon }) => (
            <button
              key={format}
              onClick={() => handleExport(format)}
              disabled={exporting !== null}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left disabled:opacity-50"
              data-testid={`export-${format}-button`}
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                {exporting === format ? (
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                ) : (
                  <Icon className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{title}</p>
                <p className="text-sm text-slate-500">{description}</p>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
